import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useUploadQueue } from '@/contexts/UploadQueueContext';
import { useUploadQueueWidget } from '@/contexts/UploadQueueWidgetContext';
import { toast } from '@/utils/toast';
import * as activeWhitelistCsv from '@/services/activeWhitelistCsv';
import {
  BulkFileTable,
  FileDropzone,
  FileUploadActions,
  TableMode,
  UploadEmptyState,
  useFileStatistics,
  useFileValidation,
  useUploadHandlers,
} from '../index';
import { MetaValidationMode, StepStatus, ValidationStep } from '../types';

// Import simple components
import { ValidationProgressBar } from './ValidationProgressBar';
import { UploadConfirmationModal } from './UploadConfirmationModal';
import { ValidationQueueModal } from './ValidationQueueModal';
import { Button, CardHeader, EmptyContent, Flex, Spinner } from '@/components/ui';
import { Link } from 'react-router-dom';
import { ROUTES } from '@/config/routes';
import { IconList, IconLock } from '@tabler/icons-react';

// import {SessionProtectionIndicator} from '../../session/SessionProtectionIndicator';

const INITIAL_STEP_STATUSES: Record<ValidationStep, StepStatus> = {
  'file-selection': 'current',
  'server-validation': 'pending',
  'ready-to-upload': 'pending',
  'upload-complete': 'pending',
};

const areStepStatusesEqual = (
  left: Record<ValidationStep, StepStatus>,
  right: Record<ValidationStep, StepStatus>
): boolean => {
  return (
    left['file-selection'] === right['file-selection'] &&
    left['server-validation'] === right['server-validation'] &&
    left['ready-to-upload'] === right['ready-to-upload'] &&
    left['upload-complete'] === right['upload-complete']
  );
};

interface BulkFileUploaderLayoutProps {
  className?: string;
  style?: React.CSSProperties;
  canUpload?: boolean;
  metaValidationMode?: MetaValidationMode;
}

export const BulkFileUploaderLayout: React.FC<BulkFileUploaderLayoutProps> = ({
  className,
  style,
  canUpload = true,
  metaValidationMode = 'server',
}) => {
  // Props are kept for future layout customization; avoid unused-vars without changing render output.
  void className;
  void style;

  // State management
  const [currentStep, setCurrentStep] =
    useState<ValidationStep>('file-selection');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [filesUploaded, setFilesUploaded] = useState(false);
  const [uploadedFileIds, setUploadedFileIds] = useState<string[]>([]);
  void uploadedFileIds;
  const [stepStatuses, setStepStatuses] = useState<
    Record<ValidationStep, StepStatus>
  >(INITIAL_STEP_STATUSES);

  // Hooks
  const fileValidation = useFileValidation({ metaValidationMode });
  const {
    addFiles: addFilesToQueue,
    state: uploadQueueState,
    startUpload,
  } = useUploadQueue();
  const { openWidget: openUploadQueue } = useUploadQueueWidget();

  // Statistics (without tableMode to avoid circular dependency)
  const {
    statistics: finalStatistics,
    duplicateFileStats,
    hasErrors,
    hasValidationResults,
    hasDuplicates,
    conflictingFiles,
  } = useFileStatistics({
    files: fileValidation.files,
    uploadQueueState,
  });
  void duplicateFileStats;

  // Currently unused (kept for the commented StrategySelector migration block)
  void conflictingFiles;

  // File work info (for potential future session logic)
  const hasFiles = fileValidation.files.length > 0;
  const isWorking =
    hasFiles &&
    (hasDuplicates || hasValidationResults || uploadQueueState.isUploading);
  void isWorking;

  // Session protection is now handled automatically by SessionProvider
  // No need for separate file work session protection

  // Handlers
  const uploadHandlers = useUploadHandlers({
    fileValidation,
    uploadQueue: { addFiles: addFilesToQueue, startUpload },
    uploadQueueWidget: { openWidget: openUploadQueue },
    onShowUploadModal: setShowUploadModal,
    onShowValidationModal: setShowValidationModal,
    onFilesUploaded: setFilesUploaded,
    onUploadedFileIds: setUploadedFileIds,
    onStepChange: setCurrentStep,
  });

  // Determine current table mode (after getting statistics)
  const currentTableMode = useMemo((): TableMode => {
    // Only switch to upload progress mode if files were uploaded in current session
    // or if there are active uploads in progress
    const hasActiveUploads = uploadQueueState.files.some(
      qf => qf.status === 'pending' || qf.status === 'uploading'
    );

    if (filesUploaded || hasActiveUploads) {
      return TableMode.UPLOAD_PROGRESS;
    }
    if (hasValidationResults) {
      return TableMode.VALIDATION;
    }
    return TableMode.DUPLICATE_CHECK;
  }, [filesUploaded, uploadQueueState.files, hasValidationResults]);

  const readyFiles = useMemo(() => {
    return fileValidation.files.filter(file => {
      if (file.validationStatus.status !== 'server-valid') return false;
      if (!file.validationStatus.serverValidation?.exists) return true;
      return file.uploadStrategy === 'overwrite';
    });
  }, [fileValidation.files]);

  const pendingTotalFiles = useMemo(() => {
    return fileValidation.files.filter(f => !f.isDuplicate).length;
  }, [fileValidation.files]);

  const handleRefreshWhitelist = useCallback(async () => {
    try {
      toast.info('Refreshing active whitelist...');
      if (typeof activeWhitelistCsv.refreshActiveWhitelistFilenameSet === 'function') {
        await activeWhitelistCsv.refreshActiveWhitelistFilenameSet();
      } else {
        // Fallback for stale module graphs / older exports.
        if (typeof activeWhitelistCsv.invalidateActiveWhitelistFilenameSetCache === 'function') {
          activeWhitelistCsv.invalidateActiveWhitelistFilenameSetCache();
        }
        await activeWhitelistCsv.getActiveWhitelistFilenameSet({ ttlMs: 0 });
      }
      toast.success('Active whitelist refreshed');
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      toast.error('Failed to refresh active whitelist', {
        description: message,
      });
    }
  }, []);

  // Calculate upload statistics for modal
  const uploadStatistics = useMemo(() => {
    const filesToUpload = readyFiles.length;
    const replacedFiles = readyFiles.filter(
      f => f.uploadStrategy === 'overwrite'
    ).length;
    const newFiles = filesToUpload - replacedFiles;

    const skippedNotApproved = fileValidation.files.filter(
      f =>
        f.validationStatus.status === 'server-invalid' ||
        (f.validationStatus.status === 'server-valid' &&
          !f.validationStatus.serverValidation?.isAllowed)
    ).length;

    const skippedDuplicates = fileValidation.files.filter(
      f => f.isDuplicate
    ).length;

    const skippedAlreadyExist = fileValidation.files.filter(
      f =>
        f.validationStatus.status === 'server-valid' &&
        f.validationStatus.serverValidation?.exists &&
        !f.uploadStrategy
    ).length;

    return {
      filesToUpload,
      newFiles,
      replacedFiles,
      skippedNotApproved,
      skippedDuplicates,
      skippedAlreadyExist,
    };
  }, [readyFiles, fileValidation.files]);

  // Update current step and step statuses based on current state
  useEffect(() => {
    const files = fileValidation.files;
    const hasFiles = files.length > 0;
    const hasDupes = hasDuplicates;

    // Reset filesUploaded when new files are added after upload completion
    // This ensures that the table mode switches back to proper mode for new cycle
    if (hasFiles && filesUploaded && currentStep === 'upload-complete') {
      const hasNewFiles = files.some(
        f => f.validationStatus.status === 'pending'
      );
      if (hasNewFiles) {
        setFilesUploaded(false);
        setUploadedFileIds(prev => (prev.length > 0 ? [] : prev));
      }
    }

    // Determine current step
    let currentStepValue: ValidationStep = 'file-selection';

    if (hasFiles) {
      if (hasDupes) {
        currentStepValue = 'file-selection';
      } else {
        const hasServerValidating = files.some(
          f => f.validationStatus.status === 'server-validating'
        );
        if (hasServerValidating) {
          currentStepValue = 'server-validation';
        } else {
          const hasServerResults = files.some(
            f =>
              f.validationStatus.status === 'server-valid' ||
              f.validationStatus.status === 'server-invalid'
          );
          if (!hasServerResults) {
            currentStepValue = 'file-selection';
          } else {
            if (readyFiles.length === 0) {
              currentStepValue = 'server-validation';
            } else if (filesUploaded) {
              currentStepValue = 'upload-complete';
            } else {
              currentStepValue = 'ready-to-upload';
            }
          }
        }
      }
    }

    // Update current step if changed
    if (currentStepValue !== currentStep) {
      setCurrentStep(currentStepValue);
    }

    // Update step statuses
    if (!hasFiles) {
      // Reset to initial state when no files
      if (filesUploaded) {
        setFilesUploaded(false);
      }
      setUploadedFileIds(prev => (prev.length > 0 ? [] : prev));
      setStepStatuses(prev =>
        areStepStatusesEqual(prev, INITIAL_STEP_STATUSES)
          ? prev
          : INITIAL_STEP_STATUSES
      );
      return;
    }

    // Update statuses based on current step
    const newStatuses: Record<ValidationStep, StepStatus> = {
      'file-selection': 'pending',
      'server-validation': 'pending',
      'ready-to-upload': 'pending',
      'upload-complete': 'pending',
    };

    // File selection status
    if (hasDupes) {
      newStatuses['file-selection'] = 'current';
    } else {
      newStatuses['file-selection'] = 'completed';
    }

    // Server validation status
    const hasServerValidating = files.some(
      f => f.validationStatus.status === 'server-validating'
    );
    const hasServerResults = files.some(
      f =>
        f.validationStatus.status === 'server-valid' ||
        f.validationStatus.status === 'server-invalid'
    );

    if (currentStepValue === 'server-validation') {
      newStatuses['server-validation'] = hasServerValidating
        ? 'processing'
        : 'current';
    } else if (hasServerResults && !hasDupes) {
      newStatuses['server-validation'] = 'completed';
    }

    // Ready to upload status
    if (currentStepValue === 'ready-to-upload') {
      newStatuses['ready-to-upload'] =
        readyFiles.length > 0 ? 'current' : 'error';
    } else if (readyFiles.length > 0 && hasServerResults && !hasDupes) {
      newStatuses['ready-to-upload'] = 'completed';
    }

    // Upload complete status
    if (currentStepValue === 'upload-complete' || filesUploaded) {
      newStatuses['upload-complete'] = 'completed';
    }

    setStepStatuses(prev =>
      areStepStatusesEqual(prev, newStatuses) ? prev : newStatuses
    );
  }, [
    fileValidation.files,
    hasDuplicates,
    hasValidationResults,
    filesUploaded,
    currentStep,
    readyFiles,
  ]);

  // Handle file drop from dropzone
  const handleFilesAdded = useCallback((files: File[]) => {
    fileValidation.addFiles(files);
  }, [fileValidation]);

  return (
    <>
      {/*<div
      className={className}
      style={{
        flex: 1,
        // display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        height: '100%',
        display: 'contents',
        ...style,
      }}
    >*/}

      {/* Strategy Selector - Will be added when StrategySelector is migrated */}
      {/* {conflictingFiles.length > 0 && (
        <StrategySelector
          conflictingFiles={conflictingFiles}
          onStrategySelect={fileValidation.setUploadStrategy}
          onBulkStrategySelect={(strategy) => {
            conflictingFiles.forEach((file) => fileValidation.setUploadStrategy(file, strategy));
          }}
        />
      )} */}

      <FileDropzone
        onFilesAdded={canUpload ? handleFilesAdded : () => {}}
        disabled={!canUpload || fileValidation.isAddingFiles}
      >
        <BulkFileTable
          files={fileValidation.files}
          metaValidationProgress={fileValidation.metaValidationProgress}
          onRemoveFile={canUpload ? uploadHandlers.handleRemoveFile : undefined}
          onRetryValidation={
            canUpload ? uploadHandlers.handleRetryValidation : undefined
          }
          onValidateAll={
            canUpload && !fileValidation.isAddingFiles
              ? uploadHandlers.handleValidateAll
              : undefined
          }
          onUploadFiles={
            canUpload && !fileValidation.isAddingFiles
              ? uploadHandlers.handleUploadFiles
              : undefined
          }
          filesUploaded={filesUploaded}
          tableMode={currentTableMode}
          statistics={finalStatistics}
          uploadQueueState={uploadQueueState}
          canUpload={canUpload}
          beforeSlot={
            canUpload &&
            fileValidation.files.length > 0 && (
              <CardHeader
                size="large"
                cardTitle={`Selected Files (${fileValidation.files.length} total)`}
                // description="  Select CSV files for import"
                endSlot={
                  <FileUploadActions
                    handlers={uploadHandlers}
                    onAddMoreFiles={uploadHandlers.handleAddMoreFiles}
                    // getInputProps will be passed from dropzone integration
                    disabled={fileValidation.isAddingFiles}
                  />
                }
              />
            )
          }
          afterSlot={
            canUpload && (
              <ValidationProgressBar
                currentStep={currentStep}
                hasErrors={hasErrors}
                stepStatuses={stepStatuses}
                onStartNew={uploadHandlers.handleClearAllFiles}
              />
            )
          }
          customEmptyContent={
            fileValidation.files.length === 0 ? (
              canUpload ? (
                fileValidation.isAddingFiles ? (
                  <EmptyContent
                    style={{ alignSelf: 'center', minHeight: '400px' }}
                    withIconBox
                    icon={<Spinner size="4" />}
                    title="Preparing files…"
                    description={
                      fileValidation.addingFilesProgress
                        ? `Scanning ${fileValidation.addingFilesProgress.scanned.toLocaleString()}/${fileValidation.addingFilesProgress.total.toLocaleString()} items…`
                        : 'Building the file list. This may take a moment for very large folders.'
                    }
                    disableAnimation
                  />
                ) : (
                  <UploadEmptyState handlers={uploadHandlers} />
                )
              ) : (
                <EmptyContent
                  style={{ alignSelf: 'center', minHeight: '400px' }}
                  withIconBox
                  icon={<IconLock style={{ color: 'var(--orange-9)' }} />}
                  title="Upload Access Required"
                  description="You don't have permission to upload files, but you can view files that are available to you."
                >
                  <Flex
                    direction="row"
                    gap="3"
                    align="center"
                    justify="center"
                    wrap="wrap"
                  >
                    <Button
                      as={Link}
                      variant="surface"
                      to={ROUTES.fileImporterFiles}
                      startIcon={<IconList />}
                    >
                      View Available Files
                    </Button>
                  </Flex>
                </EmptyContent>
              )
            ) : undefined
          }
        />
      </FileDropzone>
      {/* Progress Bar - Always show */}

      {/* Upload Confirmation Modal */}
      <UploadConfirmationModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onConfirm={uploadHandlers.handleConfirmUpload}
        statistics={uploadStatistics}
      />

      <ValidationQueueModal
        isOpen={showValidationModal}
        onClose={() => setShowValidationModal(false)}
        progress={fileValidation.metaValidationProgress}
        pendingTotalFiles={pendingTotalFiles}
        metaValidationMode={metaValidationMode as MetaValidationMode}
        onStart={uploadHandlers.handleStartValidation}
        onRevalidate={uploadHandlers.handleStartValidation}
        onCancel={fileValidation.cancelMetaValidation}
        onPause={fileValidation.pauseMetaValidation}
        onResume={fileValidation.resumeMetaValidation}
        onRetryFailed={fileValidation.retryFailedMetaValidation}
        onRefreshWhitelist={handleRefreshWhitelist}
      />
      {/*</div>*/}
    </>
  );
};
